import { stripDuplicateTrailingHashtags } from '@mondaygpt/constants'
import { MessageSquare, Share2, ThumbsUp, ThumbsDown, Video, AudioLines } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useBrandKitStore, selectBrandLogo } from '../store/brand-kit-store'
import type { YouTubeShortsContent } from '../types'
import { GeneratingMediaOverlay, GeneratingVideoPoster } from '../components/AssetMedia'
import { PreviewVideo } from './PreviewVideo'
import { VideoBeatCanvas } from '../composition/editor/VideoTimeline'
import { useVideoTimeline } from '../composition/editor/CompositionEditorProvider'
import {
  isGeneratingVideoFromFields,
  showPreviewCardGeneratingOverlay,
  toSocialHandle,
} from './preview-utils'

const SKEL = 'rgba(255, 255, 255, 0.10)'

function isYouTubeShortsEmpty(content: YouTubeShortsContent): boolean {
  return (
    !content.videoUrl &&
    !content.videoAttachmentId &&
    !content.title &&
    !content.description &&
    (content.hashtags?.length ?? 0) === 0
  )
}

interface YouTubeShortsPreviewProps {
  content: YouTubeShortsContent
  generatingFields: Set<string>
  /** Post-scoped key for shared mute state. See PreviewVideo. */
  postKey: string
  /** When false, the timeline renders read-only (falls back to PreviewVideo). Defaults to true. */
  interactive?: boolean
}

export function YouTubeShortsPreview({
  content,
  generatingFields,
  postKey,
  interactive = true,
}: YouTubeShortsPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const brandLogo = useBrandKitStore(selectBrandLogo)
  const businessName = business?.name || 'Your Business'
  // Editable video Composition → the live editable timeline; else the read-only PreviewVideo.
  const timeline = useVideoTimeline()
  const canvasTimeline = !!timeline && interactive

  if (isYouTubeShortsEmpty(content) && generatingFields.size === 0) {
    return <YouTubeShortsSkeletonPreview />
  }

  const description = stripDuplicateTrailingHashtags(content.description, content.hashtags)
  const isGeneratingVideo = isGeneratingVideoFromFields(content, generatingFields)
  const isGeneratingTitle = generatingFields.has('title')
  const isGeneratingDescription = generatingFields.has('description')
  const isGeneratingHashtags = generatingFields.has('hashtags')
  const isGenerating = generatingFields.size > 0
  const posterUrl = content.imageUrl
  const showCardOverlay = showPreviewCardGeneratingOverlay(
    isGenerating,
    isGeneratingVideo,
    posterUrl
  )

  // Editable video: the media region is the ACTIVE beat's live canvas; the beat track + controls
  // live in the composer's full-width bottom dock (ReelTimelineDock in ComposerShell).
  if (canvasTimeline) {
    return (
      <div className="relative mx-auto w-full max-w-[400px]">
        {showCardOverlay && <GeneratingMediaOverlay shape="video" tone="dark" radius={18} />}
        <VideoBeatCanvas />
      </div>
    )
  }

  return (
    <div
      className="relative mx-auto w-[320px] overflow-hidden rounded-2xl bg-black shadow-xl"
      style={{ aspectRatio: '9/16' }}
    >
      {showCardOverlay && <GeneratingMediaOverlay shape="video" tone="dark" radius={16} />}
      {/* Video area */}
      <div className="absolute inset-0">
        {content.videoUrl ? (
          <PreviewVideo
            src={content.videoUrl}
            postKey={postKey}
            className="h-full w-full object-cover"
          />
        ) : isGeneratingVideo && content.imageUrl ? (
          <GeneratingVideoPoster
            imageUrl={content.imageUrl}
            tone="dark"
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
            {!isGeneratingVideo && (
              <div className="flex flex-col items-center gap-2">
                <Video size={32} className="text-white/30" />
                <span className="text-xs text-white/30">Video preview</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* @username at top-left */}
      <div className="absolute top-4 left-4">
        <span className="text-sm font-semibold text-white drop-shadow-md">
          @{toSocialHandle(businessName)}
        </span>
      </div>

      {/* Right side actions */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-4">
        <ActionButton icon={ThumbsUp} label="0" />
        <ActionButton icon={ThumbsDown} label="Dislike" />
        <ActionButton icon={MessageSquare} label="0" />
        <ActionButton icon={Share2} label="Share" />

        {/* Audio icon */}
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10">
          <AudioLines size={16} className="text-white" />
        </div>
      </div>

      {/* Bottom content overlay */}
      <div className="absolute right-14 bottom-0 left-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-16">
        {/* Title + description */}
        <div className="relative mb-1.5">
          {content.title ? (
            <p className="text-sm leading-snug font-semibold text-white">
              {content.title.length > 80 ? content.title.slice(0, 80) + '...' : content.title}
            </p>
          ) : (
            <div
              className="h-5 w-3/4 rounded"
              style={{ background: isGeneratingTitle ? SKEL : 'rgba(255,255,255,0.1)' }}
            />
          )}
        </div>

        <div className="relative mb-1.5">
          {description ? (
            <p className="text-xs leading-relaxed text-white/80">
              {description.length > 100 ? description.slice(0, 100) + '...' : description}
            </p>
          ) : isGeneratingDescription ? (
            <div className="h-4 w-full rounded" style={{ background: SKEL }} />
          ) : null}
        </div>

        {/* Hashtags */}
        <div className="relative mb-3">
          {(content.hashtags?.length ?? 0) > 0 ? (
            <p className="text-xs text-white/60">
              {(content.hashtags ?? []).map(h => `#${h}`).join(' ')}
            </p>
          ) : isGeneratingHashtags ? (
            <div className="h-3.5 w-1/2 rounded" style={{ background: SKEL }} />
          ) : null}
        </div>

        {/* Channel row: avatar + name + subscribe (at the very bottom) */}
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full">
            {brandLogo ? (
              <img src={brandLogo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-red-600 text-[10px] font-bold text-white">
                {businessName.charAt(0)}
              </div>
            )}
          </div>
          <span className="text-xs font-semibold text-white">{businessName}</span>
          <span className="rounded bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
            Subscribe
          </span>
        </div>
      </div>
    </div>
  )
}

function ActionButton({ icon: Icon, label }: { icon: typeof ThumbsUp; label?: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Icon size={24} className="text-white" />
      {label && <span className="text-[10px] text-white">{label}</span>}
    </div>
  )
}

function YouTubeShortsSkeletonPreview() {
  return (
    <div
      className="relative mx-auto w-[320px] overflow-hidden rounded-2xl bg-gradient-to-b from-gray-100 to-gray-200 shadow-xl"
      style={{ aspectRatio: '9/16' }}
    >
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Video size={40} className="text-gray-300" />
          <span className="text-sm text-gray-400">Your YouTube Short preview will appear here</span>
          <span className="text-xs text-gray-300">Start chatting to create your video</span>
        </div>
      </div>
    </div>
  )
}
